import React, {
    useCallback,
    useEffect,
    useState,
} from "react";

import {
    useNavigate,
} from "react-router-dom";

import {
    RefreshCw,
    Search,
    X,
} from "lucide-react";

import QueryTable
    from "../components/QueryTable";

import {
    useQueries,
} from "../hooks/useQueries";

import {
    QUERY_STATUS_VALUES,
    QUERY_STATUS_LABELS,
    DEFAULT_QUERY_LIMIT,
} from "../constants/query.constants";


const QueriesPage = () => {

    const navigate = useNavigate();


    const {
        queries,
        pagination,
        loading,
        error,
        fetchQueries,
        clearError,
    } = useQueries();


    const [filters, setFilters] = useState({
        status: "",
        clientId: "",
        offeringId: "",
        assignedTo: "",
    });


    const [page, setPage] = useState(1);


    const [limit, setLimit] =
        useState(DEFAULT_QUERY_LIMIT);


    /*
    |--------------------------------------------------------------------------
    | LOAD QUERIES
    |--------------------------------------------------------------------------
    */

    const loadQueries = useCallback(
        async () => {

            try {

                await fetchQueries({
                    status:
                        filters.status || null,

                    clientId:
                        filters.clientId || null,

                    offeringId:
                        filters.offeringId || null,

                    assignedTo:
                        filters.assignedTo || null,

                    page,

                    limit,
                });

            } catch (error) {

                /*
                | Hook already handles error.
                */

                console.error(
                    "[QUERIES PAGE] Load error:",
                    error
                );

            }

        },
        [
            fetchQueries,
            filters,
            page,
            limit,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | INITIAL LOAD
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        loadQueries();

    }, [loadQueries]);


    /*
    |--------------------------------------------------------------------------
    | FILTER CHANGE
    |--------------------------------------------------------------------------
    */

    const handleFilterChange = (
        event
    ) => {

        const {
            name,
            value,
        } = event.target;


        setPage(1);


        setFilters((current) => ({
            ...current,
            [name]: value,
        }));

    };


    /*
    |--------------------------------------------------------------------------
    | CLEAR FILTERS
    |--------------------------------------------------------------------------
    */

    const clearFilters = () => {

        setFilters({
            status: "",
            clientId: "",
            offeringId: "",
            assignedTo: "",
        });


        setPage(1);

    };


    /*
    |--------------------------------------------------------------------------
    | VIEW QUERY
    |--------------------------------------------------------------------------
    */

    const handleView = (
        item
    ) => {

        navigate(
            `/queries/${item.id}`
        );
    };


    /*
    |--------------------------------------------------------------------------
    | PAGE CHANGE
    |--------------------------------------------------------------------------
    */

    const handlePrevious = () => {

        setPage((current) =>
            Math.max(
                1,
                current - 1
            )
        );

    };


    const handleNext = () => {

        const totalPages =
            pagination?.totalPages || 1;


        setPage((current) =>
            Math.min(
                totalPages,
                current + 1
            )
        );

    };


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (

        <div className="queries-page">

            {/* HEADER */}

            <div className="queries-page-header">

                <div>

                    <h1>
                        Queries
                    </h1>

                    <p>
                        Manage customer enquiries
                        submitted through the website.
                    </p>

                </div>


                <button
                    type="button"
                    className="query-refresh-button"
                    onClick={loadQueries}
                    disabled={loading}
                >

                    <RefreshCw
                        size={17}
                        className={
                            loading
                                ? "query-refresh-spinning"
                                : ""
                        }
                    />

                    {loading
                        ? "Refreshing..."
                        : "Refresh"}

                </button>

            </div>


            {/* ERROR */}

            {error && (

                <div className="query-error-message">

                    <span>
                        {error}
                    </span>


                    <button
                        type="button"
                        onClick={clearError}
                        aria-label="Close error"
                    >
                        <X size={16} />
                    </button>

                </div>

            )}


            {/* FILTERS */}

            <div className="queries-filters">

                <div className="query-filter-field">

                    <label>
                        Status
                    </label>

                    <select
                        name="status"
                        value={filters.status}
                        onChange={handleFilterChange}
                    >

                        <option value="">
                            All Statuses
                        </option>


                        {QUERY_STATUS_VALUES.map(
                            (status) => (

                                <option
                                    key={status}
                                    value={status}
                                >
                                    {
                                        QUERY_STATUS_LABELS[
                                        status
                                        ] || status
                                    }
                                </option>

                            )
                        )}

                    </select>

                </div>


                <div className="query-filter-field">

                    <label>
                        Client ID
                    </label>

                    <input
                        type="text"
                        name="clientId"
                        value={filters.clientId}
                        onChange={handleFilterChange}
                        placeholder="Client UUID"
                    />

                </div>


                <div className="query-filter-field">

                    <label>
                        Offering ID
                    </label>

                    <input
                        type="text"
                        name="offeringId"
                        value={filters.offeringId}
                        onChange={handleFilterChange}
                        placeholder="Offering UUID"
                    />

                </div>


                <div className="query-filter-field">

                    <label>
                        Assigned To
                    </label>

                    <input
                        type="text"
                        name="assignedTo"
                        value={filters.assignedTo}
                        onChange={handleFilterChange}
                        placeholder="User UUID"
                    />

                </div>


                <button
                    type="button"
                    className="query-clear-filter-button"
                    onClick={clearFilters}
                >
                    Clear
                </button>

            </div>


            {/* RESULT SUMMARY */}

            {!loading && !error && (

                <div className="queries-result-summary">

                    <div>

                        <strong>
                            {pagination?.total || 0}
                        </strong>

                        {" "}

                        {pagination?.total === 1
                            ? "query"
                            : "queries"}

                    </div>

                    <div>

                        Page{" "}
                        <strong>
                            {pagination?.page || page}
                        </strong>

                        {" "}of{" "}

                        <strong>
                            {pagination?.totalPages || 1}
                        </strong>

                    </div>

                </div>

            )}


            {/* TABLE */}

            <QueryTable
                queries={queries}
                loading={loading}
                onView={handleView}
            />


            {/* PAGINATION */}

            {!loading && queries.length > 0 && (

                <div className="queries-pagination">

                    <button
                        type="button"
                        disabled={
                            page <= 1 ||
                            loading
                        }
                        onClick={handlePrevious}
                    >
                        Previous
                    </button>


                    <span className="queries-pagination-current">
                        {pagination?.page || page}
                    </span>


                    <button
                        type="button"
                        disabled={
                            loading ||
                            page >=
                            (pagination?.totalPages || 1)
                        }
                        onClick={handleNext}
                    >
                        Next
                    </button>


                    <select
                        value={limit}
                        onChange={(event) => {

                            setLimit(
                                Number(
                                    event.target.value
                                )
                            );

                            setPage(1);

                        }}
                    >

                        <option value={10}>
                            10
                        </option>

                        <option value={20}>
                            20
                        </option>

                        <option value={50}>
                            50
                        </option>

                        <option value={100}>
                            100
                        </option>

                    </select>


                    <span>
                        per page
                    </span>

                </div>

            )}

        </div>

    );

};


export default QueriesPage;